<template>
    <div
        :class="'item item-' + ticket.slug + ' ' + ticket.status"
        v-if="ticket.status !== 'disable'"
    >
        <div class="header">
            <svg-icon class="icon" :name="ticket.location_slug"></svg-icon>
            <div class="text">
                <h3 class="title">{{ ticket.location }}</h3>
                <div
                    :class="[
                        'sector',
                        { 'with-rows': hasRows, promocode: discountCalc }
                    ]"
                >
                    <span class="name">Sector {{ ticket.sector }}</span>
                    <span class="rows">{{ ticket.rows }}</span>
                </div>
            </div>
        </div>

        <div class="content">
            <div class="information">
                <div class="group">{{ ticket.group }}</div>
                <div class="date">
                    <i class="fa fa-calendar-o"></i> {{ ticket.date }}
                </div>
                <span
                    class="ticket-tag"
                    :class="{
                        digital: ticket.is_digital,
                        physical: !ticket.is_digital
                    }"
                    @click="openModal('text', ticket)"
                >
                    {{
                        trans.tickets.table[
                            ticket.is_digital ? "is-digital" : "is-physical"
                        ]
                    }}
                </span>
            </div>

            <div v-if="ticket.includes && ticket.includes.length">
                <div
                    class="included-free"
                    v-for="include in ticket.includes"
                    :key="include.type"
                >
                    <div class="included-free-text">
                        <strong>{{ trans.tickets.included }}</strong>
                        <span>{{ include.title }}</span>
                    </div>
                    <figure class="included-free-image">
                        <img :src="include.image" :alt="include.title" />
                    </figure>
                </div>
            </div>

            <div class="price" v-if="ticket.status === 'enable'">
                <div class="price-heading">
                    <div v-if="outlet" class="price-from">
                        From:
                        <span
                            >{{ trans.tickets.table.currency }}
                            {{ ticket.price_ref }}</span
                        >
                    </div>

                    <ticket-tooltip
                        position="soft-right"
                        :mobile="true"
                        :ticket="ticket"
                        :config="config"
                        :icons="trans.tickets.table.icons"
                    ></ticket-tooltip>
                </div>

                <span v-if="outlet" class="to-text">To: </span>
                <span class="to"
                    >{{ trans.tickets.table.currency }} {{ ticket.price }}</span
                >
            </div>

            <!-- Forced deactivation of the purchase button -->
            <button
                v-if="ticket.status === 'enable'"
                type="button"
                class="button button-submit"
                @click="book(ticket.id)"
            >
                {{ trans.buttons.buy }}
            </button>

            <button
                v-if="ticket.status !== 'enable'"
                type="button"
                class="button button-submit"
                disabled
            >
                {{ ticket.status_text || trans.tickets.table[ticket.status] }}
            </button>

            <div v-if="ticket.deal && ticket.deal_title" class="deal-note">
                <div class="deal-icon">
                    <i :class="'fa ' + icons[ticket.deal]"></i>
                </div>
                <div class="deal-content">
                    <b>{{ ticket.deal_title }}</b>
                    <span>{{ ticket.deal_text }}</span>
                </div>
            </div>
        </div>

        <div class="buttons-cta">
            <button
                v-if="ticket.map"
                class="button button-outline"
                @click="openModal('map', ticket)"
            >
                <i class="fa fa-map-o"></i>
                <span class="text">{{ trans.tickets.table.map }}</span>
            </button>
            <button
                v-if="ticket.img && ticket.img.length"
                class="button button-outline"
                @click="openModal('img', ticket)"
            >
                <i class="fa fa-picture-o"></i>
                <span class="text">
                    {{
                        ticket.imgs.length > 1
                            ? trans.tickets.table.images
                            : trans.tickets.table.image
                    }}
                </span>
            </button>
            <button
                v-if="ticket.description"
                class="button button-outline"
                @click="openModal('description', ticket)"
            >
                <i class="fa fa-file-text-o"></i>
                <span class="text">{{ trans.tickets.table.description }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import SvgIcon from "../general/SvgIcon.vue";
import TicketTooltip from "./TicketsTableTooltip.vue";

export default {
    props: {
        openModal: Function,
        promotionalInfo: Object,
        outlet: {
            type: Boolean,
            required: false
        },
        ticket: {
            type: Object,
            required: true
        },
        icons: {
            type: Array,
            required: true
        },
        config: {
            type: Object,
            required: true
        },
        trans: {
            type: Object,
            required: true
        }
    },

    components: {
        SvgIcon,
        TicketTooltip
    },

    data() {
        return {};
    },

    computed: {
        hasRows() {
            return this.ticket.rows !== "";
        },
        discountCalc() {
            return this.isValidPromocode() ? "promocode" : "";
        }
    },

    methods: {
        book(id) {
            const form_action = this.config.form_action;
            const separator = form_action.indexOf("\?") < 0 ? "?" : "&";
            window.location =
                this.config.form_action + separator + "Ticket=" + id;
        },

        isValidPromocode() {
            this.backupPrices();

            if (!this.promotionalInfo) {
                this.setPrice(); // 100%
                return false;
            }

            if (this.promotionalInfo.tickets.indexOf(this.ticket.id) !== -1) {
                this.setPrice(this.promotionalInfo.discount, this.promotionalInfo.promo_type);
                return true;
            }

            return false;
        },

        backupPrices() {
            if (!this.ticket.pricesList) {
                const { price, price_ref } = this.ticket;
                this.ticket.pricesList = { price, price_ref };
            }
        },

        setPrice(discount = 1, promoType = null) {
            const priceList = this.ticket.pricesList;
            const price = priceList.price;

            let priceWithDiscount

            if (promoType === 'fixed') {
                // Fixed discount: subtract the discount amount
                priceWithDiscount = Math.floor(price - discount)
            } else {
                // Percent discount: multiply by the discount factor
                priceWithDiscount = Math.floor(price * discount)
            }

            this.ticket.price_ref = price;
            this.ticket.price = priceWithDiscount;
        }
    }
};
</script>

<style lang="scss">
@import "../../../sass/variables";

@each $section, $color in $tabs-bookers {
    .item-#{$section} {
        .header .icon {
            &:before {
                background: $color;
                opacity: 0.1;
            }

            svg {
                width: 100%;
                height: 100%;
            }

            path {
                fill: $color;
            }
        }
    }
}
</style>

<style lang="scss" scoped>
@import "../../../sass/variables";

@each $section, $color in $tabs-bookers {
    .item-#{$section} {
        .sector {
            color: $color;
            &.with-rows {
                border-color: $color;
            }
            .name {
                background: $color;
            }
        }
        .title {
            color: $color;
        }
    }
}

.item {
    position: relative;
    background: #fff;
    box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.1);
    border-radius: 15px;
    margin-bottom: 32px;
    border: 1px solid var(--color-gray-light);
}

.header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    z-index: 0;
    padding: 20px 16px 16px 20px;
    border-bottom: 1px solid var(--color-gray-light);

    .icon {
        position: relative;
        width: 48px;
        height: 48px;
        margin-right: 12px;

        &:before {
            content: "";
            position: absolute;
            left: -20%;
            top: -20%;
            width: 100%;
            height: 0;
            padding-top: 0;
            padding-bottom: 100%;
            min-width: 27px;
            background: rgba(var(--color-purple-darker-rgb));
            z-index: 0;
            border-radius: 10px;
        }
    }

    .title {
        font-size: 1.6rem;
        line-height: 1em;
    }

    .sector {
        display: inline-block;
        text-align: center;
        border-radius: 15px;
        background: #fff;
        border: 1px solid transparent;

        &.with-rows {
            .name {
                border-top-right-radius: 0;
            }

            .name,
            .rows {
                padding: 0 16px;
            }

            .rows {
                display: inline-block;
                padding: 0 16px 0 8px;
            }
        }

        .name {
            display: inline-block;
            color: #fff;
            border-radius: 15px;
            padding: 0 16px;
            font-weight: bold;
        }
    }
}

.information,
.price {
    padding: 16px;
}

.information {
    font-size: 1.3rem;
    border-bottom: 1px solid var(--color-gray-light);

    .group {
        color: var(--color-gray-medium-content);
    }

    .date {
        color: var(--color-gray-dark-content);
    }

    .fa {
        margin-right: 4px;
    }
}

.price-heading {
    display: flex;
    justify-content: space-between;
}

.tooltips {
    border: solid var(--color-gray-light);
    border-width: 0 0 2px 2px;
    padding: 4px 10px;
    border-bottom-left-radius: 15px;
}

.price {
    padding-top: 0;
    padding-right: 0;
    font-size: 1.1rem;

    .price-from {
        color: var(--color-red);
        padding-top: 10px;

        span {
            text-decoration: line-through;
        }
    }

    .to-text {
        color: var(--color-gray-medium-content);
    }

    .to {
        font-size: 2.2rem;
        line-height: 1em;
        color: var(--color-purple-darker);
    }
}

.buttons-cta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
    border-top: 1px solid var(--color-gray-light);

    .button {
        border: solid var(--color-gray-light);
        border-width: 0 1px 0 0;
        border-radius: 0;
        width: calc(100% / 3);
        padding: 0;
        color: var(--color-gray-medium-content);
        line-height: 0.7rem;
        font-size: 0.9rem;
        font-weight: normal;
        height: 52px;

        &:hover {
            color: #fff;
        }

        &:last-child {
            border: 0;
        }

        .fa {
            margin: 0 0 4px;
        }
    }

    .text {
        display: block;
    }
}

.button-submit {
    display: block;
    font-size: 1.45rem;
    color: #fff;
    margin: 0 auto;
    margin-top: 16px;
    font-weight: bold;
    width: calc(100% - 32px);
}
</style>

<style lang="scss" scoped>
.deal-note {
    padding: 16px 10px 0px 10px;
    display: flex;
    gap: 8px;

    .fa {
        background: #949393;
        border-radius: 100%;
        font-size: 13px;
        color: white;
        text-align: center;
        width: 22px;
        height: 22px;
        line-height: 22px;

        &.koko-deal {
            background: url("/images/tickets/koko-deal.svg");
            filter: grayscale(100%);
        }
    }

    span {
        display: block;
    }
}

.ticket-tag {
    display: inline-block;
    padding: 5px 15px;
    font-size: 10px;
    border-radius: 20px;
    text-align: center;
    font-weight: bold;
    border: 1px solid;
}

.digital {
    color: #72c8fa;
}

.physical {
    color: #ef3b22;
}
</style>

<style lang="scss" scoped>
.included-free {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #fffaf0;
    gap: 16px;

    &-text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        strong {
            font-size: 16px;
            font-weight: 700;
            text-transform: uppercase;
            color: #a47a00;
        }

        span {
            font-size: 18px;
        }
    }

    &-image {
        margin: 0;
        overflow: hidden;
        border-radius: 8px;
        width: 100px;
        height: 100px;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }
    }
}
</style>
